/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useEffect, useState } from 'react';
import { Grid, FormControl, Autocomplete, TextFieldProps } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { useLocation, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../../hooks';
import { TPTextField } from '../../../../components/Inputs';
import { TTrialState } from './Calendar';
import { getCountry, getUrlConvertedString } from '../../../../helper';
import { getTutor } from '../../../../store/V2/TutorSlice/api';
import { getTutorsState } from '../../../../store/V2/TutorSlice';
import { IUserSubject } from '../../../../store/UserSlice';

const convertSubjectsToCurriculums = (subjects: IUserSubject[]) => {
  const curriculumMap = new Map<string, Map<string, Set<string>>>();

  subjects.forEach(({ curriculum, subject, grade }) => {
    if (!curriculumMap.has(curriculum)) {
      curriculumMap.set(curriculum, new Map());
    }

    const subjectsMap = curriculumMap.get(curriculum)!;

    if (!subjectsMap.has(subject)) {
      subjectsMap.set(subject, new Set());
    }

    subjectsMap.get(subject)!.add(grade);
  });

  return Array.from(curriculumMap.entries()).map(
    ([curriculum, subjectsMap]) => ({
      curriculum,
      subjects: Array.from(subjectsMap.entries()).map(([subject, grades]) => ({
        subject,
        grade: Array.from(grades).map((g) => ({ grade: g })),
      })),
    })
  );
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      borderRadius: '12px',
    },
  },
};

type TSelectOption = {
  label: string;
  value: string;
};

interface FilterSectionProps {
  state: TTrialState;
  updateStateAttributes: (attributes: Partial<TTrialState>) => void;
}

const InputSection: React.FC<FilterSectionProps> = ({
  state,
  updateStateAttributes,
}) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const urlParams = useParams();

  const tutorId = urlParams.id;

  const urlQueryParams = new URLSearchParams(location.search);
  const urlCurriculum = urlQueryParams.get('curriculum');
  const urlSubject = urlQueryParams.get('subject');
  const urlGrade = urlQueryParams.get('grade');

  // Store
  const { tutorData } = useAppSelector(getTutorsState);

  // State
  const [curriculumOptions, setCurriculumOptions] = useState<
    Array<TSelectOption>
  >([]);
  const [subjectOptions, setSubjectOptions] = useState<Array<TSelectOption>>(
    []
  );
  const [gradeOptions, setGradeOptions] = useState<Array<TSelectOption>>([]);

  const tutorCurriculums = tutorData?.subjects
    ? convertSubjectsToCurriculums(tutorData.subjects)
    : [];

  useEffect(() => {
    if (tutorId && state.selectedCurriculum && state.selectedGrade) {
      dispatch(
        getTutor({
          id: tutorId,
          country: getCountry(),
          curriculum: state.selectedCurriculum.value,
          grade: state.selectedGrade.value,
        })
      );
    }
  }, [state.selectedGrade?.value]);

  useEffect(() => {
    if (!tutorCurriculums.length) return;

    const newCurriculumOptions = tutorCurriculums.map(({ curriculum }) => ({
      label: curriculum,
      value: curriculum,
    }));
    setCurriculumOptions(newCurriculumOptions);

    let selectedCurriculum;
    if (newCurriculumOptions.length === 1) {
      [selectedCurriculum] = newCurriculumOptions;
    } else if (urlCurriculum) {
      selectedCurriculum = newCurriculumOptions.find(
        (option) => getUrlConvertedString(option.value) === urlCurriculum
      );
    }

    if (!selectedCurriculum) return;

    updateStateAttributes({ selectedCurriculum });

    const matchingCurriculum = tutorCurriculums.find(
      (item) => item.curriculum === selectedCurriculum.value
    );
    if (!matchingCurriculum?.subjects?.length) return;

    const newSubjectOptions = matchingCurriculum.subjects.map(
      ({ subject }) => ({
        label: subject,
        value: subject,
      })
    );
    setSubjectOptions(newSubjectOptions);

    let selectedSubject;
    if (newSubjectOptions.length === 1) {
      [selectedSubject] = newSubjectOptions;
    } else if (urlSubject) {
      selectedSubject = newSubjectOptions.find(
        (option) => getUrlConvertedString(option.value) === urlSubject
      );
    }

    if (!selectedSubject) return;

    updateStateAttributes({ selectedSubject });

    const matchingSubject = matchingCurriculum.subjects.find(
      (item) => item.subject === selectedSubject.value
    );
    if (!matchingSubject?.grade?.length) return;

    const newGradeOptions = matchingSubject.grade.map(({ grade }) => ({
      label: grade,
      value: grade,
    }));
    setGradeOptions(newGradeOptions);

    let selectedGrade;
    if (newGradeOptions.length === 1) {
      [selectedGrade] = newGradeOptions;
    } else if (urlGrade) {
      selectedGrade = newGradeOptions.find(
        (option) => getUrlConvertedString(option.value) === urlGrade
      );
    }

    if (!selectedGrade) return;

    updateStateAttributes({ selectedGrade });
  }, [tutorData?.subjects]);

  const handleCurriculumChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    const newSubjectOptions: TSelectOption[] = [];

    if (tutorCurriculums && tutorCurriculums.length && value) {
      const selectedCurriculumData = tutorCurriculums.find(
        (item) => item.curriculum === value.value
      );

      if (
        selectedCurriculumData &&
        selectedCurriculumData.subjects &&
        selectedCurriculumData.subjects.length
      ) {
        selectedCurriculumData.subjects.forEach((item) => {
          newSubjectOptions.push({
            label: item.subject,
            value: item.subject,
          });
        });
      }
    }

    updateStateAttributes({
      selectedCurriculum: value,
      selectedSubject: null,
      selectedGrade: null,
    });

    setSubjectOptions(newSubjectOptions);
    setGradeOptions([]);
  };

  const handleSubjectChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    const newGradeOptions: TSelectOption[] = [];

    if (state.selectedCurriculum) {
      const selectedCurriculumData = tutorCurriculums.find(
        (curriculumItem) =>
          curriculumItem.curriculum === state.selectedCurriculum?.value
      );

      if (
        selectedCurriculumData &&
        selectedCurriculumData.subjects &&
        selectedCurriculumData.subjects.length
      ) {
        const selectedSubject = selectedCurriculumData.subjects.find(
          (subjectItem) => subjectItem.subject === value?.value
        );

        if (
          selectedSubject &&
          selectedSubject.grade &&
          selectedSubject.grade.length
        ) {
          selectedSubject.grade.forEach((gradeItem) => {
            newGradeOptions.push({
              label: gradeItem.grade,
              value: gradeItem.grade,
            });
          });
        }
      }
    }

    updateStateAttributes({
      selectedSubject: value,
      selectedGrade: null,
    });

    setGradeOptions(newGradeOptions);
  };

  const handleGradeChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    updateStateAttributes({
      selectedGrade: value,
    });
  };

  return (
    // <Collapse in={state.isParamsPassed}>
    <Grid container spacing={2} marginBottom={2}>
      <Grid item xs={12} sm={4} md={12}>
        <FormControl fullWidth>
          <Autocomplete
            value={state.selectedCurriculum}
            options={curriculumOptions}
            onChange={handleCurriculumChange}
            popupIcon={<KeyboardArrowDownIcon fontSize="small" />}
            isOptionEqualToValue={(option, value) =>
              option.label === value.value
            }
            getOptionKey={(option: TSelectOption) => option.value}
            renderInput={(params) => (
              <TPTextField
                {...params}
                {...textFieldProps}
                placeholder="Select Curriculum"
              />
            )}
          />
        </FormControl>
      </Grid>
      <Grid item xs={6} sm={4} md={6}>
        <FormControl fullWidth>
          <Autocomplete
            value={state.selectedSubject}
            options={subjectOptions}
            onChange={handleSubjectChange}
            disabled={!state.selectedCurriculum}
            popupIcon={<KeyboardArrowDownIcon fontSize="small" />}
            isOptionEqualToValue={(option, value) =>
              option.label === value.value
            }
            getOptionKey={(option: TSelectOption) => option.value}
            renderInput={(params) => (
              <TPTextField
                {...params}
                {...textFieldProps}
                placeholder="Select Subject"
              />
            )}
          />
        </FormControl>
      </Grid>
      <Grid item xs={6} sm={4} md={6}>
        <FormControl fullWidth>
          <Autocomplete
            value={state.selectedGrade}
            options={gradeOptions}
            onChange={handleGradeChange}
            disabled={!state.selectedSubject}
            popupIcon={<KeyboardArrowDownIcon fontSize="small" />}
            isOptionEqualToValue={(option, value) =>
              option.label === value.value
            }
            getOptionKey={(option: TSelectOption) => option.value}
            renderInput={(params) => (
              <TPTextField
                {...params}
                {...textFieldProps}
                placeholder="Select Grade"
              />
            )}
          />
        </FormControl>
      </Grid>
    </Grid>
    // </Collapse>
  );
};

export default InputSection;
